import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Button,
  Typography,
  TablePagination,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import styles from "./Bookings.module.css";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import Dropdown from "../Dropdown/Dropdown1";
import { useNavigate, useParams } from "react-router-dom";
import { useUserContext } from "../../../Context/UserContext";
import { useLoaderContext, useToastContext } from "../../../Context/ToastContext";
import { Helmet } from "react-helmet";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AddCardIcon from '@mui/icons-material/AddCard';
import { apiGet, apiPut } from "../../../utils/ApiManager";

const fetchRestaurantsBooking =
  process.env.REACT_APP_FETCH_RESTAURANTS_VIEW_BOOKING;
const fetchBookingList = process.env.REACT_APP_FETCH_BOOKING_LIST;
const cancelBooking = process.env.REACT_APP_HANDLE_CANCEL_BOOKING;
const refundBooking = process.env.REACT_APP_HANDLE_REFUND_BOOKING;

const Bookings = () => {
  const [page, setPage] = useState(1);
  const { requestConfirmation } = useUserContext();
  const { showToast } = useToastContext();
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [status, setStatus] = useState("");
  const [selectedRestaurantId, setSelectedRestaurantId] = useState("");
  const [restaurantList, setRestaurantList] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [userId, setUserId] = useState(useParams().userId || "");
  const { setLoading } = useLoaderContext()
  const navigateTo = useNavigate();

  const statusOptions = [
    { label: "Confirmed", value: "confirmed" },
    { label: "Rejected", value: "rejected" },
    { label: "Cancelled", value: "cancelled" },
    { label: "Dining", value: "dining" },
    { label: "Payment Due", value: "paymentdue" },
    { label: "Completed", value: "completed" },
    { label: "Auto Cancelled", value: "auto-cancelled" },
  ];

  useEffect(() => {
    fetchRestBookings();
    // fetchBookings();
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage]);

  const handleChange = (event) => {
    const { name, value } = event?.target;

    if (name === "restaurant") {
      setSelectedRestaurantId(value);
    } else if (name === "status") {
      setStatus(value);
    } else if (name === "searchQuery") {
      setSearchQuery(value);
    } else if (name === "userId") {
      setUserId(value);
    } else if (name === "bookingId") {
      setBookings(value);
    } else if (name === 'selectedDate') {
      setSelectedDate(value);
    }
  };

  const fetchRestBookings = async () => {
    try {
      setLoading(true);
      const response = await apiGet(fetchRestaurantsBooking);
      if (response?.success) {
        setRestaurantList(response.data?.restaurantList || []);
      } else {
        showToast(
          response?.message || "Failed to fetch bookings !",
          "error"
        );
        setRestaurantList([]);
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Failed to fetch bookings !",
        "error"
      );
      setRestaurantList([]);
    } finally {
      setLoading(false)
    }
  };

  const fetchBookings = async () => {
    try {
      const params = {
        pageNo: page,
        pageSize: rowsPerPage,
        ...(searchQuery && { bookingId: searchQuery }),
        ...(userId && { userId }),
        ...(selectedDate && {
          bookingDate: new Date(selectedDate),
        }),
        ...(status && { status }),
        ...(selectedRestaurantId && { restaurantId: selectedRestaurantId }),
      };
      const queryString = new URLSearchParams(params).toString();
      const response = await apiGet(`${fetchBookingList}?${queryString}`);
      if (response?.success) {
        setBookings(response?.data || []);
        setTotalCount(response?.totalCount || 0);
      } else {
        showToast(
          response?.message || "Failed to fetch bookings !",
          "error"
        );
        setBookings([])
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Failed to fetch bookings !",
        "error"
      );
      setBookings([])

    }
  };

  const handleFilter = () => {
    setPage(1);
    setTimeout(() => fetchBookings(), 0);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setUserId("");
    setSelectedDate(null);
    setStatus("");
    setSelectedRestaurantId("");
    setPage(1);
    fetchBookings();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(1);
  };

  const handleView = (bookingId) => {
    navigateTo(`/bookings/${bookingId}`);
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      const result = await requestConfirmation(
        `Want to cancel booking - ${bookingId}?`
      );
      if (!result) return;
      setLoading(true)
      const response = await apiPut(`${cancelBooking}/${bookingId}`, {});
      if (response?.success) {
        showToast(
          response?.message || "Booking cancelled successfully !",
          "success"
        );
        fetchBookings();
      } else {
        showToast(
          response?.message || "Failed to cancel booking !",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Failed to cancel booking !",
        "error"
      );
    } finally {
      setLoading(false)
    }
  };

  const handleUpdateRefund = async (bookingId) => {
    try {
      if (!bookingId) {
        showToast("Booking ID Not provided !")
      }
      const result = await requestConfirmation("Want to update this dining as refunded ?")
      if (!result) return
      setLoading(true)
      const response = await apiPut(`${refundBooking}/${bookingId}`, {})
      if (response?.success) {
        showToast(response?.message || "Booking marked as refunded !", 'success')
        fetchBookings();
      } else {
        showToast(response?.message || "Failed to update for refund !", 'error')
      }
    } catch (error) {
      showToast(error.response?.message || error?.message || "Failed to update for refund !", 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box className={styles.mainContainer}>
      <Helmet>
        <title>Bookings | DiNEST</title>
        <meta name="description" content="View bookings on DiNEST" />
      </Helmet>

      <Typography variant="h4" className={styles.containerTitle}>
        Diner Bookings
      </Typography>

      {/* Filters */}
      <Box className={styles.filterContainer}>
        <Grid className={styles.inputGroup}>
          <TextField
            label="Search User ID"
            variant="outlined"
            value={userId}
            name="userId"
            type="text"
            className={styles.inputTextfield}
            onChange={handleChange}
          />
        </Grid>

        <Grid className={styles.inputGroup}>
          <TextField
            label="Search Booking ID"
            variant="outlined"
            value={searchQuery}
            name="searchQuery"
            className={styles.inputTextfield}
            onChange={handleChange}
          />
        </Grid>

        <Grid className={styles.inputGroup}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label="Select Date"
              value={selectedDate}
              className={styles.inputTextfield}
              format="DD/MM/YYYY"
              inputFormat="DD/MM/YYYY"
              name="selectedDate"
              onChange={(e) => setSelectedDate(e)}
            />
          </LocalizationProvider>
        </Grid>

        <Grid className={styles.inputGroup}>
          <Dropdown
            label="Restaurant"
            fullWidth
            name="restaurant"
            value={selectedRestaurantId}
            onChange={(e) => handleChange(e, "selectedRestaurantId")}
            options={restaurantList}
            required
          />
        </Grid>

        <Grid className={styles.inputGroup}>
          <Dropdown
            label="Status"
            fullWidth
            name="status"
            value={status}
            onChange={(e) => handleChange(e, "status")}
            options={statusOptions}
            required
          />
        </Grid>
      </Box>

      <Box className={styles.buttonsContainer}>
        <Button
          className={styles.clearBtn}
          variant="outlined"
          color="error"
          onClick={handleClearFilters}
        >
          Clear Filters
        </Button>
        <Button
          className={styles.saveBtn}
          variant="contained"
          sx={{ backgroundColor: "#207d46" }}
          onClick={handleFilter}
        >
          Apply Filters
        </Button>
      </Box>

      {/* Bookings List */}
      {bookings.length > 0 ? (
        <>
          <Box className={styles.bookingsContainer}>
            <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
                           All Bookings
                        </Typography>

            <Box container spacing={5} className={styles.innerGridParent}>
              {bookings.map((booking) => (
                <Grid key={booking.bookingId} className={styles.cardsContainer}>
                  <Card
                    className={`${styles.card} ${["completed", "confirmed", "dining"].includes(booking.bookingStatus)
                      ? styles.activeCard
                      : styles.inactiveCard
                      }`}
                  >
                    {/* Bottom-right action buttons */}
                    {booking.bookingStatus !== "dining" && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 1,
                          position: "absolute",
                          bottom: 5,
                          right: 5,
                        }}
                      >
                        {/* CreditScoreIcon */}
                        {/* View Button */}
                        {(['rejected', 'cancelled'].includes(booking.bookingStatus) && booking?.isRefundable) && <Tooltip arrow title={booking?.isRefunded ? "Refunded" : "Update Refunded"}>
                          <IconButton
                            onClick={() => {
                              if (booking?.isRefunded) return;
                              handleUpdateRefund(booking?.bookingId)
                            }}
                            sx={{
                              color: "#207d46",
                            }}
                          >
                            {booking?.isRefunded ? <CreditScoreIcon /> : <AddCardIcon />}
                          </IconButton>
                        </Tooltip>}
                        <Tooltip arrow title="View Booking">
                          <IconButton
                            onClick={() => handleView(booking.bookingId)}
                            sx={{
                              color: ["completed", "confirmed", "dining"].includes(
                                booking.bookingStatus
                              )
                                ? "#207d46"
                                : "red",
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                        {/* Cancel Button */}
                        {booking.bookingStatus === "confirmed" && (
                          <Tooltip arrow title="Cancel Booking">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelBooking(booking.bookingId);
                              }}
                              sx={{ color: "red" }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}

                    {/* Card Content */}
                    <CardContent className={styles.cardContent}>
                      <Typography
                        gutterBottom
                        sx={{
                          mt: 2,
                          fontWeight: "600",
                          color: ["completed", "confirmed", "dining"].includes(
                            booking.bookingStatus
                          )
                            ? "#207d46"
                            : "red",
                        }}
                      >
                        {booking.bookingId}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <h4
                          style={{
                            textTransform: "uppercase",
                            color: ["completed", "confirmed", "dining"].includes(
                              booking.bookingStatus
                            )
                              ? "#207d46"
                              : "red",
                          }}
                        >
                          {booking.bookingStatus}
                        </h4>
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <strong>Restaurant:</strong> {booking.restaurantName || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Date:</strong>{" "}
                        {new Date(booking.bookingDate || "N/A").toLocaleDateString("en-GB")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Time:</strong> {booking.startTime || "N/A"} - {booking.endTime}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Started At:</strong> {booking.startedAt ? new Date(booking.startedAt).toLocaleDateString("en-GB",{hour: '2-digit', minute: '2-digit', second: '2-digit'}) : "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Diner:</strong> {booking.bookedBy?.firstName || "N/A"}{" "}
                        {booking.bookedBy?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Total Bill:</strong>{" "}
                        <span className={styles.paidTag}>
                          {booking.subTotalAmount && booking.subTotalAmount > 0
                            ? `₹${(booking.subTotalAmount - (booking?.discountAmount)) + (booking.gstAmount ||0)}/-`
                            : "N/A"}
                        </span>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Partial Paid:</strong>{" "}
                        <span className={styles.paidTag}>
                          {booking.payments?.find((p) => p.isPartial)?.paidAmount
                            ? `₹${booking.payments.find((p) => p.isPartial).paidAmount}/-`
                            : "N/A"}
                        </span>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Final Paid:</strong>{" "}
                        <span className={styles.paidTag}>
                          {booking.payments?.find((p) => p.isPartial === false)?.paidAmount
                            ? `₹${booking.payments.find((p) => p.isPartial === false).paidAmount}/-`
                            : "N/A"}
                        </span>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Box>
          </Box>

          <div className={styles.paginationContainer}>
            <TablePagination
              className={styles.pagination}
              rowsPerPageOptions={[2, 5, 10, 15]}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count, page }) =>
                `Page ${page + 1} of ${Math.ceil(
                  count / rowsPerPage
                )} (results ${from}-${to} of ${count})`
              }
              sx={{
                "& .MuiTablePagination-actions button": {
                  backgroundColor: "transparent",
                  color: "white !important",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                  "&:focus": { outline: "3px solid #2196F3 !important" },
                },
                "& .MuiTablePagination-actions button svg": {
                  fill: "white !important",
                },
                "& .MuiSelect-root": {
                  color: "white !important",
                },
                "& .MuiSelect-icon": {
                  fill: "white !important",
                },
              }}
              slotProps={{
                select: { "aria-label": "Rows per page" },
                actions: {
                  showFirstButton: true,
                  showLastButton: true,
                  slots: {
                    firstPageIcon: FirstPageRoundedIcon,
                    lastPageIcon: LastPageRoundedIcon,
                    nextPageIcon: ChevronRightRoundedIcon,
                    backPageIcon: ChevronLeftRoundedIcon,
                  },
                },
              }}
            />
          </div>
        </>
      ) : (
        <h2 style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>No bookings yet!</h2>
      )}
    </Box>
  );
};
export default Bookings;
